/* ============================================================
   Curtain PDP  –  section-main-product-curtain.js

   Behaviour for the blocks the curtain template adds on top of the
   theme's own product page. Everything the theme already owns - the
   media slider, thumbnails, zoom, variant picker, quantity input and
   the made-to-measure customizer - is left alone; this file only wires
   the new blocks and listens to the theme's events.

   All hooks are data-attributes so markup and behaviour stay separate.
   ============================================================ */

(function () {
  'use strict';

  var money = function (cents) {
    if (window.Woolman && Woolman.Utils && Woolman.Utils.formatMoney) {
      return Woolman.Utils.formatMoney(cents);
    }
    return (cents / 100).toFixed(0) + ' kr';
  };

  /* ---------- equal-width variant pills -----------------------
     The stylesheet sizes the pills from --rd-seg-w with a hardcoded
     fallback. Without this the fallback wins, so the pills are a fixed
     width regardless of their labels - fine for "132 cm", too narrow for a
     longer value. Measure the widest label's natural width and lock every
     pill to it, so the rows stay equal length and nothing clips.
     Re-measured on resize, once webfonts land, and after a variant change,
     because the theme re-renders the option labels by id.          */
  function segmentWidth(root) {
    var panel = root.querySelector('.rd-options');
    if (!panel) return;

    function measure() {
      var pills = panel.querySelectorAll('.product-form__input label');
      // a zero-width panel (hidden tab, preview) would measure to nothing and
      // lock the pills to a wrong width - skip and retry on the next resize
      if (!pills.length || !panel.getBoundingClientRect().width) return;

      panel.style.removeProperty('--rd-seg-w');
      panel.classList.add('rd-mater');            // width: max-content
      var widest = 0;
      Array.prototype.forEach.call(pills, function (l) {
        widest = Math.max(widest, l.getBoundingClientRect().width);
      });
      panel.classList.remove('rd-mater');
      if (widest) panel.style.setProperty('--rd-seg-w', Math.ceil(widest) + 'px');
    }

    measure();
    window.addEventListener('resize', debounce(measure, 150));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    // the theme swaps variant-selects on change, replacing the label elements
    root.addEventListener('change', function (e) {
      if (e.target.closest('.product-form__input')) setTimeout(measure, 0);
    });
    document.addEventListener('variant:changed', function () { setTimeout(measure, 0); });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  }

  /* ---------- made-to-measure height field ---------------------
     Revealed when the shopper picks the option value that means
     "made to measure". Writes a line item property, and locks the
     buy button until a valid measurement is entered.            */
  function customHeight(root) {
    var wrap = root.querySelector('[data-curtain-height]');
    if (!wrap) return;

    var input = wrap.querySelector('[data-curtain-height-input]');
    var trigger = (wrap.dataset.triggerValue || '').trim();
    var min = parseInt(wrap.dataset.min, 10) || 0;
    var max = parseInt(wrap.dataset.max, 10) || 0;
    var form = root.querySelector('product-form form, form[data-type="add-to-cart-form"]');
    if (!input || !form) return;

    var submit = form.querySelector('[type="submit"]');
    // The theme repaints the button on variant change, so the label element is
    // re-queried each time and its own text is remembered as the natural one.
    var naturalLabel = '';
    function labelEl() {
      return submit && submit.querySelector('[data-cta-label]');
    }

    function selectedValues() {
      return Array.prototype.map.call(
        root.querySelectorAll('.product-form__input input:checked, .product-form__input select'),
        function (el) { return (el.value || '').trim(); }
      );
    }

    function isActive() {
      if (!trigger) return false;
      return selectedValues().indexOf(trigger) !== -1;
    }

    function valid() {
      var v = parseInt(input.value, 10);
      if (isNaN(v)) return false;
      if (min && v < min) return false;
      if (max && v > max) return false;
      return true;
    }

    // Never touch submit.disabled: the theme owns that, and it means
    // "this variant cannot be bought". Enabling it here would let a sold-out
    // variant be added the moment a measurement was typed. Instead the lock
    // is advisory (aria-disabled + a class) and enforced by blocking submit.
    function locked() {
      return isActive() && !valid();
    }

    function sync() {
      var active = isActive();
      wrap.classList.toggle('is-visible', active);
      wrap.hidden = !active;
      input.disabled = !active;

      if (!submit) return;
      var label = labelEl();
      var prompt = wrap.dataset.promptLabel || 'Ange mått';

      if (locked()) {
        // remember whatever the theme last rendered, before overwriting it
        if (label && label.textContent.trim() !== prompt) {
          naturalLabel = label.textContent;
        }
        submit.classList.add('is-measure-locked');
        submit.setAttribute('aria-disabled', 'true');
        if (label) label.textContent = prompt;
      } else {
        submit.classList.remove('is-measure-locked');
        // clear the hint only if the theme is not itself disabling the button
        if (!submit.disabled) submit.removeAttribute('aria-disabled');
        if (label && naturalLabel && label.textContent.trim() === prompt) {
          label.textContent = naturalLabel;
        }
      }
      root.dispatchEvent(new CustomEvent('curtain:measure', {
        bubbles: true,
        detail: { active: active, value: parseInt(input.value, 10) || null }
      }));
    }

    function block(e) {
      if (!locked()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      input.focus();
      input.reportValidity && input.reportValidity();
    }

    // capture phase, so this runs before the theme's product-form handler
    form.addEventListener('submit', block, true);
    if (submit) submit.addEventListener('click', block, true);

    input.addEventListener('input', sync);
    root.addEventListener('change', function (e) {
      if (!e.target.closest('.product-form__input')) return;
      sync();
      // and again after the theme has repainted the button for the new variant
      setTimeout(sync, 0);
      setTimeout(sync, 250);
    });
    sync();
  }

  /* ---------- free shipping meter ------------------------------
     Mirrors cart.js:104. The threshold is in SHOP currency while the cart
     total is in presentment currency, so the cart total is normalised back to
     shop currency before comparing, and the remainder is formatted forward
     again by formatMoney. On a cart change the whole block is re-rendered from
     the server, because two of the three exclusions (a product tag and a
     metafield) are not visible in /cart.js.                      */
  function shippingMeter(root) {
    var meter = root.querySelector('[data-curtain-shipping]');
    if (!meter) return;

    var threshold = parseInt(meter.dataset.threshold, 10) || 0;
    var fill = meter.querySelector('[data-shipping-fill]');
    var text = meter.querySelector('[data-shipping-text]');
    if (!threshold) return;

    function paint(cartTotalPresentment) {
      var cartTotal = cartTotalPresentment;
      if (meter.dataset.shopCurrency !== meter.dataset.targetCurrency) {
        var rate = parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
        cartTotal = Math.round(cartTotal / rate);
      }
      var left = Math.max(0, threshold - cartTotal);
      var pct = Math.max(0, Math.min(100, (cartTotal / threshold) * 100));
      if (fill) fill.style.width = pct + '%';
      if (!text) return;
      if (left === 0) {
        text.innerHTML = meter.dataset.reachedText || '';
      } else {
        var tpl = meter.dataset.remainingText || '';
        text.innerHTML = tpl.replace('[amount]', '<strong>' + money(left) + '</strong>');
      }
    }

    // Re-render from the server so the exclusion rules are re-evaluated: a
    // "no free shipping" tag or the custom.no_free_shipping metafield cannot be
    // seen from /cart.js, only the _Long_Goods line item property can.
    function refresh() {
      var section = meter.dataset.section;
      if (!section) return;
      fetch(window.location.pathname + '?section_id=' + encodeURIComponent(section))
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-curtain-shipping], [data-shipping-not-eligible]');
          var current = root.querySelector('[data-curtain-shipping], [data-shipping-not-eligible]');
          if (!fresh || !current || !current.parentNode) return;
          current.parentNode.replaceChild(fresh, current);
          init(root);                      // rebind against the new markup
        })
        .catch(function () { /* leave the server-rendered state alone */ });
    }

    paint(parseInt(meter.dataset.cartTotal, 10) || 0);
    document.addEventListener('ajaxProduct:added', refresh);
    document.addEventListener('cart:updated', refresh);
  }

  /* ---------- sticky buy bar -----------------------------------
     Appears once the real buy button has scrolled UP out of view.

     It must not appear while the buy button is still below the fold,
     which is the state you land in below desktop: there the buy panel
     sits under a full-height gallery, so on a 375x812 screen the button
     starts around 1290px down. "Not intersecting" is true in both
     directions, so testing it alone showed the bar on arrival at the
     top. boundingClientRect.top < 0 distinguishes the two: combined
     with !isIntersecting it means the anchor is fully above the
     viewport, i.e. genuinely scrolled past.                        */
  function stickyBar(root) {
    var bar = document.querySelector('[data-curtain-sticky]');
    var anchor = root.querySelector('.product-block__buy_buttons');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    new IntersectionObserver(function (entries) {
      var entry = entries[0];
      var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', scrolledPast);
    }, { rootMargin: '0px 0px -80px 0px' }).observe(anchor);

    var cta = bar.querySelector('[data-sticky-cta]');
    if (cta) {
      cta.addEventListener('click', function () {
        var submit = root.querySelector('product-form [type="submit"]');
        if (submit && !submit.disabled) return submit.click();
        anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    // mirror the live price and the selected variant summary
    var priceOut = bar.querySelector('[data-sticky-price]');
    var variantOut = bar.querySelector('[data-sticky-variant]');

    function mirrorPrice() {
      if (!priceOut) return;
      var price = root.querySelector('.price .price-item--regular, .price__regular .price-item');
      var txt = price ? price.textContent.trim() : '';
      // renderProductInfo fires TWICE per switch and the first pass renders an
      // empty price wrapper; writing that would blank the bar for ~400 ms.
      if (txt) priceOut.textContent = txt;
    }

    function mirrorVariant() {
      if (!variantOut) return;
      var parts = Array.prototype.map.call(
        root.querySelectorAll('.product-form__input input:checked'),
        function (el) { return (el.value || '').trim(); }
      ).filter(Boolean);
      variantOut.textContent = parts.join(' · ');
    }

    // The radio is checked synchronously, so the variant summary is already
    // correct on change. The PRICE is not - it arrives with the refetch.
    root.addEventListener('change', mirrorVariant);

    /* Same signal as the delivery estimate below: variant:changed is never
       dispatched, and the raw change event fires BEFORE the refetch lands, so
       mirroring on it pinned the bar to the previous variant's price (268 cm
       selected, bar still reading the 132 cm price). Watch the node
       renderProductInfo actually rewrites instead. Coalesced with setTimeout,
       not rAF - rAF is paused in a background tab and would latch `queued`. */
    var queued = false;
    var priceNode = document.getElementById('price-' + root.id);
    if (priceNode && 'MutationObserver' in window) {
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        setTimeout(function () { queued = false; mirrorPrice(); }, 0);
      }).observe(priceNode, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class']
      });
    }

    mirrorPrice();
    mirrorVariant();
  }

  /* ---------- FAQ accordion ------------------------------------
     <details> keeps working without JS; this only adds the height
     transition, so a closed panel is still readable to crawlers. */
  function faq(root) {
    var DUR = 340;
    root.querySelectorAll('[data-curtain-faq-item]').forEach(function (item) {
      var body = item.querySelector('[data-curtain-faq-body]');
      var summary = item.querySelector('summary');
      if (!body || !summary) return;
      var timer;

      summary.addEventListener('click', function (e) {
        e.preventDefault();
        clearTimeout(timer);

        if (item.open) {
          body.style.height = body.scrollHeight + 'px';
          requestAnimationFrame(function () { body.style.height = '0px'; });
          timer = setTimeout(function () {
            item.open = false;
            body.style.height = '';
          }, DUR);
        } else {
          item.open = true;
          body.style.height = '0px';
          requestAnimationFrame(function () {
            body.style.height = body.scrollHeight + 'px';
          });
          timer = setTimeout(function () { body.style.height = ''; }, DUR);
        }
      });
    });
  }

  /* ---------- help-row link that opens an accordion item --------
     A help-row link keeps its URL setting as its href, which is what
     no-JS visitors get and what a product without the item falls back
     to (the item is skipped when its content is empty). When the item
     is on the page, the click opens it through its own summary, so it
     animates like a normal click, and scrolls it to the top. html's
     scroll-padding-top already clears the fixed header. Modified clicks
     still go to the page, so "open in new tab" works.
     data-curtain-faq-contains (Link 1, the sample) also requires the item
     to link to that URL - otherwise the click follows the href.       */
  function faqLinks(root) {
    root.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('[data-curtain-faq-link]');
      if (!link || e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      // both sides are handleized in Liquid, so the key is a safe token
      var key = link.getAttribute('data-curtain-faq-link');
      var item = root.querySelector('[data-curtain-faq-key="' + key + '"]');
      var summary = item && item.querySelector('summary');
      if (!summary) return;

      var contains = link.getAttribute('data-curtain-faq-contains');
      if (contains && !item.querySelector('a[href*="' + contains.replace(/["\\]/g, '\\$&') + '"]')) return;

      e.preventDefault();
      if (!item.open) summary.click();
      summary.focus({ preventScroll: true });
      item.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* ---------- colour swatch hover label ------------------------
     Hovering a sibling swatch previews that colour's name in the
     group label, then restores the selected one.                 */
  function swatchLabel(root) {
    var labelEl = root.querySelector('.product-block__sibling_products strong');
    if (!labelEl) return;
    var selected = labelEl.textContent.trim();

    root.querySelectorAll('.rd-options .sibling-product').forEach(function (link) {
      var nameEl = link.querySelector('.visually-hidden');
      if (!nameEl) return;
      var name = nameEl.textContent.trim();
      var show = function () { labelEl.textContent = name; };
      var reset = function () { labelEl.textContent = selected; };
      link.addEventListener('mouseenter', show);
      link.addEventListener('mouseleave', reset);
      link.addEventListener('focus', show);
      link.addEventListener('blur', reset);
    });
  }

  /* ---------- colour swatch hover -> hero preview ---------------
     Hovering a swatch lays that colour's own main image over the hero,
     so the range can be compared without a page hop per colour. The
     image comes from data-preview, which curtain-colour-swatches.liquid
     only emits on the OTHER colours - the current one is already on
     screen, so its swatch stays inert.

     A layer on top of the slider rather than a src swap on the slide:
     the theme owns that DOM (scroll position, srcset, lazy loading, the
     zoom modal's item ids), so nothing has to be restored on the way out
     and a mid-hover variant change cannot strand a foreign image in the
     gallery. The stylesheet aligns the layer with the hero box.

     Pointer-driven and hover-capable only. On touch there is no hover,
     and the synthetic pointerenter a tap fires would flash the preview
     for the few frames before the navigation it belongs to.         */
  function swatchPreview(root) {
    var slider = root.querySelector('.product-media-slider');
    var row = root.querySelector('.product-block__sibling_products');
    if (!slider || !row) return;
    if (!window.matchMedia || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var layer = null;
    // bumped on every show/hide, so a slow image that lands after the pointer
    // has moved on is dropped instead of overwriting the newer one
    var turn = 0;

    function overlay() {
      if (!layer || !layer.isConnected) {
        layer = document.createElement('img');
        layer.className = 'rd-swatch-preview';
        layer.alt = '';
        layer.setAttribute('aria-hidden', 'true');
        layer.setAttribute('draggable', 'false');
        slider.appendChild(layer);
      }
      return layer;
    }

    function show(src) {
      if (!src) return;
      var el = overlay();
      var mine = ++turn;
      var pre = new Image();
      var apply = function () {
        pre.onload = null;
        if (mine !== turn) return;
        el.src = src;
        el.classList.add('is-visible');
      };
      // only reveal once the bytes are in, otherwise the first hover of each
      // colour fades in an empty box while the image is still downloading
      pre.onload = apply;
      pre.src = src;
      if (pre.complete) apply();
    }

    function hide() {
      turn++;
      if (layer) layer.classList.remove('is-visible');
    }

    // Warm the previews once the pointer reaches the buy panel. Measured cold
    // on the dev store, the first hover of a colour otherwise waits ~1s for its
    // image while the swatch sits there doing nothing; by the time the pointer
    // has crossed the panel to the swatch row the bytes are usually in. Nothing
    // is fetched for a visitor who only ever touches the gallery.
    var panel = row.closest('.rd-options') || row;
    panel.addEventListener('pointerenter', function () {
      row.querySelectorAll('a.sibling-product[data-preview]').forEach(function (link) {
        new Image().src = link.dataset.preview;
      });
    }, { once: true });

    row.querySelectorAll('a.sibling-product[data-preview]').forEach(function (link) {
      link.addEventListener('pointerenter', function (e) {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        show(link.dataset.preview);
      });
      link.addEventListener('pointerleave', hide);
      link.addEventListener('focus', function () { show(link.dataset.preview); });
      link.addEventListener('blur', hide);
    });

    // a fast sweep off the row can outrun a swatch's own pointerleave
    row.addEventListener('pointerleave', hide);
  }

  /* ---------- accessory ranges: pills + show more ---------------
     One pill per range, one panel each, and a foot that reveals the rows
     the block keeps back. Replaces the carousel, whose arrows hid both the
     ranges and how much was behind them.

     Every row is already in the page - the pills and the foot only flip
     `hidden` - so switching range costs no request and cannot reflow the
     buy panel underneath.                                            */
  function upsell(root) {
    var block = root.querySelector('[data-curtain-upsell]');
    if (!block) return;

    var pills = Array.prototype.slice.call(block.querySelectorAll('[data-curtain-upsell-pill]'));

    function select(pill) {
      pills.forEach(function (p) {
        var on = p === pill;
        p.classList.toggle('is-aktiv', on);
        p.setAttribute('aria-selected', on ? 'true' : 'false');
        // roving tabindex: the selected pill is the one in the tab order
        p.tabIndex = on ? 0 : -1;
        var panel = document.getElementById(p.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
    }

    pills.forEach(function (pill, i) {
      pill.addEventListener('click', function () { select(pill); });

      // arrow / home / end move between ranges - what a screen reader expects
      // of a tablist, and the other half of the roving tabindex above
      pill.addEventListener('keydown', function (e) {
        var to = null;
        if (e.key === 'ArrowRight') to = (i + 1) % pills.length;
        else if (e.key === 'ArrowLeft') to = (i - 1 + pills.length) % pills.length;
        else if (e.key === 'Home') to = 0;
        else if (e.key === 'End') to = pills.length - 1;
        if (to === null) return;
        e.preventDefault();
        select(pills[to]);
        pills[to].focus();
      });
    });

    block.querySelectorAll('[data-curtain-upsell-more]').forEach(function (btn) {
      var panel = btn.closest('.rd-up-panel');
      if (!panel) return;
      var extras = Array.prototype.slice.call(panel.querySelectorAll('[data-upsell-extra]'));
      var label = btn.querySelector('.rd-up-mer-text');

      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        extras.forEach(function (row) { row.hidden = open; });
        if (label) {
          label.textContent = open ? btn.dataset.labelMore : btn.dataset.labelLess;
        }
      });
    });

    quickAdd(root);
  }

  /* ---------- quick add ----------------------------------------
     The prototype's button had no handler. This adds the item,
     acknowledges on the button and bumps the header cart count.  */
  function quickAdd(root) {
    root.querySelectorAll('[data-curtain-quick-add]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var id = btn.dataset.variantId;
        if (!id || btn.classList.contains('is-tillagd')) return;

        btn.disabled = true;
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('cart');
            return r.json();
          })
          .then(function () {
            btn.classList.add('is-tillagd');
            bumpBlip();
            document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
            setTimeout(function () {
              btn.classList.remove('is-tillagd');
              btn.disabled = false;
            }, 1600);
          })
          .catch(function () {
            btn.disabled = false;
          });
      });
    });
  }

  function bumpBlip() {
    var blip = document.querySelector('.cart-blip');
    if (!blip) return;
    var count = parseInt(blip.textContent, 10);
    blip.textContent = isNaN(count) ? '1' : String(count + 1);
    blip.hidden = false;
    var sr = document.querySelector('[data-cart-count-sr]');
    if (sr) sr.textContent = blip.textContent;
  }

  /* ---------- shop-the-look lightbox --------------------------- */
  function ugc(root) {
    var modal = document.querySelector('[data-curtain-ugc-modal]');
    if (!modal) return;

    var img = modal.querySelector('[data-ugc-image]');
    var name = modal.querySelector('[data-ugc-name]');
    var list = modal.querySelector('[data-ugc-products]');

    function close() {
      modal.hidden = true;
      document.body.style.overflow = '';
    }

    function open(card) {
      if (img) {
        img.src = card.dataset.image || '';
        img.alt = card.dataset.imageAlt || '';
      }
      if (name) name.textContent = card.dataset.name || '';
      if (list) {
        var scope = card.parentNode;   // the card's own wrapper holds its <template>
        var source = scope && scope.querySelector('[data-ugc-card-products]');
        list.innerHTML = source ? source.innerHTML : '';
        quickAdd(modal);
      }
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      var closeBtn = modal.querySelector('[data-ugc-close]');
      if (closeBtn) closeBtn.focus();
    }

    root.querySelectorAll('[data-curtain-ugc-card]').forEach(function (card) {
      card.addEventListener('click', function () { open(card); });
    });
    modal.querySelectorAll('[data-ugc-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }

  /* ---------- reserve the shop-the-look slot -------------------
     The Cevoid gallery injects its markup about 400ms after load,
     which grows the document by its own height under anyone already
     reading near it. Measured at 167px on a 375px viewport.

     Reserving the height up front and releasing it once the app has
     rendered keeps the document height constant across the injection,
     without leaving a gap on products whose gallery is empty - the
     block's settings hide it in that case, and the release still runs
     on the timeout.                                                */
  var SHOP_SLOT_RESERVE = 167;

  function reserveShopSlot(root) {
    var shop = root.querySelector('.rd-shop');
    var slot = shop && shop.querySelector('.shopify-block');
    if (!shop || !slot) return;

    // The app ships a server-rendered shell (its own block div plus
    // style/script tags) and fills it later, so child count says
    // nothing - only the rendered height does.
    if (slot.getBoundingClientRect().height >= SHOP_SLOT_RESERVE) return;

    // Reserve on the section, leaving the slot free to be measured.
    var base = Math.round(shop.getBoundingClientRect().height);
    shop.style.minHeight = (base + SHOP_SLOT_RESERVE) + 'px';

    var released = false;
    function release() {
      if (released) return;
      released = true;
      shop.style.minHeight = '';
    }

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () {
        // most of the way there is enough; the last pixels are the
        // app's own images settling and are not worth holding for
        if (slot.getBoundingClientRect().height < SHOP_SLOT_RESERVE * 0.6) return;
        ro.disconnect();
        release();
      });
      ro.observe(slot);
      setTimeout(function () { ro.disconnect(); release(); }, 5000);
    } else {
      setTimeout(release, 5000);
    }
  }

  /* ---------- colour switch continuity -------------------------
     Each colour is its own product, so a swatch is a real <a> and
     switching colour is a full navigation - the browser lands the new
     page at scroll 0 and the shopper loses the row they were reading.

     This keeps the swatch row at the same viewport offset across the
     hop, so the page appears to stay put and only the contents change.
     It is a progressive enhancement: with JS off, or when anything
     below is missing, the link behaves exactly as before.

     Only applied to a fresh 'navigate'. On back/forward the browser has
     its own (better) scroll restoration and must be left alone.     */
  function siblingNav(root) {
    var KEY = 'curtain:sibling-nav';
    var row = root.querySelector('.product-block__sibling_products');
    if (!row) return;

    root.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a.sibling-product');
      if (!link || link.classList.contains('current-product')) return;
      try {
        sessionStorage.setItem(KEY, JSON.stringify({
          offset: Math.round(row.getBoundingClientRect().top),
          t: Date.now()
        }));
      } catch (err) { /* private mode: fall back to a normal navigation */ }

      // Carry a ?view= override across the hop. Only present while
      // previewing the template before the sibling products have their
      // template assigned in the admin; in production there is no
      // param and the href is left untouched.
      //
      // Built through the URL API rather than string concatenation because
      // the href ends in the #MainContent fragment - appending "?view=..."
      // to that would land inside the fragment and be lost.
      var view = new URLSearchParams(location.search).get('view');
      if (!view) return;
      try {
        var next = new URL(link.href, location.href);
        if (!next.searchParams.has('view')) {
          next.searchParams.set('view', view);
          link.href = next.toString();
        }
      } catch (err) { /* leave the href as authored */ }
    });

    var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (nav && nav.type !== 'navigate') return;

    var raw = null;
    try {
      raw = sessionStorage.getItem(KEY);
      sessionStorage.removeItem(KEY);
    } catch (err) { return; }
    if (!raw) return;

    var saved;
    try { saved = JSON.parse(raw); } catch (err) { return; }
    // only honour a hop that just happened, never a stale tab
    if (!saved || typeof saved.offset !== 'number' || Date.now() - saved.t > 10000) return;

    // Stop the moment the shopper takes over; never fight a real scroll.
    var userMoved = false;
    ['wheel', 'touchstart', 'keydown'].forEach(function (ev) {
      window.addEventListener(ev, function () { userMoved = true; }, { once: true, passive: true });
    });

    function place() {
      if (userMoved) return;
      var y = row.getBoundingClientRect().top + window.pageYOffset - saved.offset;
      // The theme sets scroll-behavior:smooth on <html>, so a plain
      // scrollTo here animates - which both looks like the lurch this
      // is meant to remove and loses races against the browser's own
      // scroll-to-top on a fresh navigation. Force it to jump.
      var docEl = document.documentElement;
      var prev = docEl.style.scrollBehavior;
      docEl.style.scrollBehavior = 'auto';
      window.scrollTo(0, Math.max(0, Math.round(y)));
      docEl.style.scrollBehavior = prev;
    }

    // The gallery above the row has no final height until its images
    // decode, so a single pass lands short and then visibly corrects -
    // the same lurch from the other direction. Re-place on every change
    // in document height until it holds still, which converges during
    // load and is therefore never seen.
    place();
    var deadline = Date.now() + 2000;
    var lastHeight = -1;
    (function settle() {
      var h = document.documentElement.scrollHeight;
      if (h !== lastHeight) {
        lastHeight = h;
        place();
      }
      if (!userMoved && Date.now() < deadline) requestAnimationFrame(settle);
    })();
    window.addEventListener('load', place, { once: true });
  }

  /* ---------- boot --------------------------------------------- */
  function init(root) {
    if (!root) return;
    // the shipping meter re-renders itself and calls init again, so only the
    // one-time bindings are guarded
    if (root.dataset.curtainReady) {
      shippingMeter(root);
      return;
    }
    root.dataset.curtainReady = 'true';
    segmentWidth(root);
    customHeight(root);
    shippingMeter(root);
    stickyBar(root);
    reserveShopSlot(root);
    siblingNav(root);
    faq(root);
    faqLinks(root);
    swatchLabel(root);
    deliveryEstimate(root);
    swatchPreview(root);
    upsell(root);
    ugc(root);
  }

  /* ---------- delivery date estimate --------------------------
     Turns the store's stated lead times into real dates:
     "Leverans 18-24 sep" rather than "inom 2-6 arbetsdagar".

     Client-side on purpose. The same line rendered in Liquid would be
     baked into Shopify's full-page cache and could be served a day stale;
     the snippet therefore ships a plain business-day sentence and this
     overwrites it on load. Dates are resolved in Europe/Stockholm, not the
     shopper's zone - the promise is about when the warehouse ships.       */
  function deliveryEstimate(root) {
    var box = root.querySelector('[data-curtain-delivery]');
    if (!box) return;
    var out = box.querySelector('[data-curtain-delivery-text]');
    if (!out) return;

    var d = box.dataset;
    var num = function (v, fallback) {
      var n = parseInt(v, 10);
      return isNaN(n) ? fallback : n;
    };

    /* Easter Sunday, anonymous Gregorian algorithm. Every moving Swedish
       red day is an offset from it, so this is the only hard part. */
    function easter(year) {
      var a = year % 19, b = Math.floor(year / 100), c = year % 100;
      var dd = Math.floor(b / 4), e = b % 4;
      var f = Math.floor((b + 8) / 25);
      var g = Math.floor((b - f + 1) / 3);
      var h = (19 * a + b - dd - g + 15) % 30;
      var i = Math.floor(c / 4), k = c % 4;
      var l = (32 + 2 * e + 2 * i - h - k) % 7;
      var m = Math.floor((a + 11 * h + 22 * l) / 451);
      var month = Math.floor((h + l - 7 * m + 114) / 31);
      var day = ((h + l - 7 * m + 114) % 31) + 1;
      return new Date(year, month - 1, day);
    }

    var holidayCache = {};
    /* Weekday red days only - Saturdays and Sundays are already excluded, so
       Midsommardagen and Alla helgons dag never need listing. Julafton,
       midsommarafton and nyårsafton are not official holidays but the
       warehouse is shut, and erring long is the safe direction here. */
    function holidays(year) {
      if (holidayCache[year]) return holidayCache[year];
      var key = function (dt) { return dt.getFullYear() + '-' + dt.getMonth() + '-' + dt.getDate(); };
      var e = easter(year);
      var off = function (n) { return new Date(year, e.getMonth(), e.getDate() + n); };
      var list = [
        new Date(year, 0, 1),   // nyårsdagen
        new Date(year, 0, 6),   // trettondedag jul
        off(-2),                // långfredag
        off(1),                 // annandag påsk
        new Date(year, 4, 1),   // första maj
        off(39),                // Kristi himmelsfärdsdag
        new Date(year, 5, 6),   // nationaldagen
        new Date(year, 11, 24), // julafton
        new Date(year, 11, 25), // juldagen
        new Date(year, 11, 26), // annandag jul
        new Date(year, 11, 31)  // nyårsafton
      ];
      // midsommarafton: the Friday that falls on 19-25 June
      for (var day = 19; day <= 25; day++) {
        var cand = new Date(year, 5, day);
        if (cand.getDay() === 5) { list.push(cand); break; }
      }
      var set = {};
      list.forEach(function (dt) { set[key(dt)] = true; });
      holidayCache[year] = { set: set, key: key };
      return holidayCache[year];
    }

    function isBusinessDay(dt) {
      var wd = dt.getDay();
      if (wd === 0 || wd === 6) return false;
      var h = holidays(dt.getFullYear());
      return !h.set[h.key(dt)];
    }

    function addBusinessDays(from, n) {
      var r = new Date(from.getTime()), added = 0;
      while (added < n) {
        r.setDate(r.getDate() + 1);
        if (isBusinessDay(r)) added++;
      }
      return r;
    }

    function snapForward(from) {
      var r = new Date(from.getTime());
      while (!isBusinessDay(r)) r.setDate(r.getDate() + 1);
      return r;
    }

    /* "now" as the warehouse sees it. sv-SE formats as YYYY-MM-DD HH:MM:SS,
       so the parts can be read straight off without a second Date round-trip. */
    function stockholmNow() {
      var parts;
      try {
        parts = new Date().toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' });
      } catch (err) {
        parts = null;
      }
      var m = parts && parts.match(/^(\d{4})-(\d{2})-(\d{2})\D+(\d{2})/);
      if (!m) {
        var local = new Date();
        return { date: new Date(local.getFullYear(), local.getMonth(), local.getDate()), hour: local.getHours() };
      }
      return {
        date: new Date(+m[1], +m[2] - 1, +m[3]),
        hour: +m[4]
      };
    }

    /* The page's own language (<html lang>, layout/theme.liquid). Swedish
       keeps the hand-built range below; other languages get Intl's range,
       which knows their day/month order ("Oct 3 – 6", "3.–6.10."). */
    var pageLang = (document.documentElement.lang || 'sv').toLowerCase();
    var swedish = pageLang.indexOf('sv') === 0;

    function label(dt) {
      // sv-SE short months carry a trailing period ("18 sep."); drop it
      return dt.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }).replace(/\.$/, '');
    }

    function range(a, b) {
      if (!swedish) {
        try {
          var fmt = new Intl.DateTimeFormat(pageLang, { day: 'numeric', month: 'short' });
          if (a.getTime() === b.getTime()) return fmt.format(b);
          return fmt.formatRange ? fmt.formatRange(a, b) : fmt.format(a) + '–' + fmt.format(b);
        } catch (err) { /* unknown language tag: Swedish below */ }
      }
      if (a.getTime() === b.getTime()) return label(b);
      if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
        return a.getDate() + '–' + label(b);
      }
      return label(a) + '–' + label(b);
    }

    /* Made to measure is read off the customizer rather than kept in a
       variable: that element IS in the theme's variant swap list, so it is
       always current after a change, while this block never gets swapped.
       data-custom is the first-paint value for products with no customizer. */
    function isCustom() {
      var cz = document.getElementById('customizer-' + root.id);
      if (!cz) return d.custom === 'true';
      if (cz.classList.contains('hidden')) return false;
      return cz.innerHTML.trim().length > 0;
    }

    function render() {
      var now = stockholmNow();
      var start = new Date(now.date.getTime());
      var cutoff = num(d.cutoff, 0);
      if (cutoff > 0 && now.hour >= cutoff) start.setDate(start.getDate() + 1);

      var custom = isCustom();
      // made to measure with the sy upp add-on ticked (assets/sy-upp.js): its
      // days and its own line, from the section root
      var syupp = custom && root.classList.contains('rd-syupp-on') && root.dataset.syuppDelivery;
      var first, last;
      if (syupp) {
        first = last = snapForward(new Date(start.getFullYear(), start.getMonth(), start.getDate() + num(root.dataset.syuppDays, 7)));
      } else if (custom) {
        first = snapForward(new Date(start.getFullYear(), start.getMonth(), start.getDate() + num(d.customMin, 21)));
        last = snapForward(new Date(start.getFullYear(), start.getMonth(), start.getDate() + num(d.customMax, 21)));
      } else {
        first = addBusinessDays(start, num(d.warehouseMin, 1) + num(d.transitMin, 1));
        last = addBusinessDays(start, num(d.warehouseMax, 3) + num(d.transitMax, 3));
      }
      if (last < first) last = first;

      // both labels are the block setting or the theme's translation
      // (products.pdp), same as the shipping meter's; sy upp's is always
      // the theme's. The empty check only guards a missing attribute.
      var tpl = syupp || (custom ? (d.labelCustom || '') : (d.label || ''));
      if (!tpl) return;
      out.innerHTML = tpl.split('[range]').join(range(first, last));
    }

    render();

    /* Nothing dispatches variant:changed in this theme, and the raw change
       event fires before the refetch lands, so neither is usable. The price
       and customizer nodes are rewritten by renderProductInfo on every
       variant change - watching them is the one reliable signal. Coalesced
       into a frame because that swap writes several nodes in a row. */
    var queued = false;
    var recalc = function () {
      if (queued) return;
      queued = true;
      // setTimeout rather than requestAnimationFrame: rAF is paused in a
      // background tab, which would leave `queued` latched true and kill
      // every later recalculation until the tab is looked at again.
      setTimeout(function () { queued = false; render(); }, 0);
    };
    ['price-', 'customizer-'].forEach(function (prefix) {
      var node = document.getElementById(prefix + root.id);
      if (!node) return;
      new MutationObserver(recalc).observe(node, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class']
      });
    });
    // the sy upp add-on ticked or unticked
    root.addEventListener('syupp:change', recalc);
  }

  function boot() {
    document.querySelectorAll('[data-curtain-pdp]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // re-init after a theme editor section reload
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-curtain-pdp]');
    if (root) init(root);
  });
})();
